import React, { useState, useEffect } from "react";
import { Button, DatePicker, Input, message, Select, Spin } from "antd";
import dayjs from "dayjs";
import { getRequest, postRequest } from "../service/index";
import { getEmployeeId } from "../utils/localStorage";

const { Option } = Select;

const NewLoan = ({ onClose, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(true);
  const [formData, setFormData] = useState({
    loanDate: "",
    numberOfMonths: "",
    loanAmount: "",
    employeeId: "",
  });
  const approvedBy = getEmployeeId;
  // Fetch employees on mount
  useEffect(() => {
    const fetchEmployees = async () => {
      setEmployeeLoading(true);
      try {
        const res = await getRequest("/api/companies/reportto");
        setEmployees(res.employees || []);
      } catch (err) {
        message.error("Failed to fetch employees");
        setEmployees([]);
      } finally {
        setEmployeeLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  // Calculate per month instalment
  const perMonthInstalment =
    formData.loanAmount && formData.numberOfMonths
      ? Number(formData.loanAmount) / Number(formData.numberOfMonths)
      : "";

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleDateChange = (date, dateString) => {
    setFormData((prev) => ({
      ...prev,
      loanDate: dateString
    }));
  };

  const handleEmployeeChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      employeeId: value
    }));
  };

  // Fix: Defensive filterOption for Select to avoid crash on search
  const employeeFilterOption = (input, option) => {
    if (!option) return false;
    if (typeof option.children === "string") {
      return option.children.toLowerCase().includes(input.toLowerCase());
    }
    // If children is an array or ReactNode, try to extract text
    // AntD Option children is usually a string or array of strings
    let text = "";
    if (Array.isArray(option.children)) {
      text = option.children
        .map((child) => (typeof child === "string" ? child : ""))
        .join(" ");
    } else if (option.children && typeof option.children === "object" && "props" in option.children) {
      // Try to get text from nested ReactNode
      text = option.children.props && option.children.props.children
        ? String(option.children.props.children)
        : "";
    }
    if (!text && option.children) {
      text = String(option.children);
    }
    return text.toLowerCase().includes(input.toLowerCase());
  };

  const handleSubmit = async () => {
    if (
      !formData.loanDate ||
      !formData.numberOfMonths ||
      !formData.loanAmount ||
      !formData.employeeId
    ) {
      message.error("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      // Compose payload for API
      const payload = {
        ...formData,
        perMonthInstalment,
        approvedBy
      };
      // Call /api/loanpayment with postRequest
      await postRequest("/api/loanpayment", payload);
      // Optionally call onSubmit for parent (if needed)
      if (onSubmit) {
        await onSubmit(payload);
      }
      message.success("Loan request submitted!");
      if (onClose) onClose();
    } catch (error) {
      message.error("Failed to submit loan request.");
      console.error("Error submitting loan:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 flex flex-col gap-1">
          <label htmlFor="employeeId" className="text-md">
            Employee
          </label>
          {employeeLoading ? (
            <Spin />
          ) : (
            <Select
              id="employeeId"
              className="w-full"
              placeholder="Select employee"
              value={formData.employeeId || undefined}
              onChange={handleEmployeeChange}
              showSearch
              optionFilterProp="children"
              filterOption={employeeFilterOption}
            >
              {employees.map((emp) => (
                <Option key={emp._id} value={emp._id}>
                  {emp.name}{" "}
                  {emp.department ? `(${emp.department}${emp.designation ? `, ${emp.designation}` : ""})` : ""}
                </Option>
              ))}
            </Select>
          )}
        </div>
        <div className="col-span-6 flex flex-col gap-1">
          <label htmlFor="loanDate" className="text-md">
            Loan Date
          </label>
          <DatePicker
            id="loanDate"
            className="w-full"
            value={formData.loanDate ? dayjs(formData.loanDate) : null}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            placeholder="Select loan date"
          />
        </div>
        <div className="col-span-6 flex flex-col gap-1">
          <label htmlFor="numberOfMonths" className="text-md">
            Number of Months
          </label>
          <Input
            type="number"
            id="numberOfMonths"
            className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md"
            placeholder="Enter number of months"
            value={formData.numberOfMonths}
            onChange={handleInputChange}
            min={1}
          />
        </div>
        <div className="col-span-6 flex flex-col gap-1">
          <label htmlFor="loanAmount" className="text-md">
            Loan Amount
          </label>
          <Input
            type="number"
            id="loanAmount"
            className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md"
            placeholder="Enter loan amount"
            value={formData.loanAmount}
            onChange={handleInputChange}
            min={1}
          />
        </div>
        <div className="col-span-6 flex flex-col gap-1">
          <label className="text-md">
            Per Month Instalment
          </label>
          <Input
            value={perMonthInstalment ? perMonthInstalment : ""}
            readOnly
            className="border border-gray-400 outline-hidden px-2 py-1 rounded-md text-md bg-gray-100"
            placeholder="Calculated automatically"
          />
        </div>
      </div>
      <div className="flex items-center justify-end gap-3 my-2">
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          loading={loading}
          type="primary"
          className="bg-primary text-white"
        >
          {loading ? "Submitting..." : "Submit Loan"}
        </Button>
      </div>
    </div>
  );
};

export default NewLoan;
